import axios from "axios";
import {jwtDecode} from "jwt-decode";

interface DecodedToken {
    exp: number;

    [key: string]: any;
}

const endpointRest = `${process.env.REACT_APP_AIS_ENDPOINT!}`;

const ApiHttpClient = () => {
    let client = axios.create({baseURL: endpointRest});
    const accessToken = localStorage.getItem("token");
    const refreshTokenExpiry = localStorage.getItem("refreshTokenExpiryTime");

    if (accessToken && refreshTokenExpiry && new Date(refreshTokenExpiry) > new Date()) {
        client.defaults.headers.common["Authorization"] =
            "Bearer " + accessToken;
    } else {
        window.location.href = "/login";
    }

    client.interceptors.request.use(async (config) => {
        const accessToken = localStorage.getItem("token");
        const refreshToken = localStorage.getItem("refreshToken");
        const refreshTokenExpiry = localStorage.getItem("refreshTokenExpiryTime");

        if (accessToken && refreshToken && refreshTokenExpiry && new Date(refreshTokenExpiry) > new Date()) {
            if (!isTokenValid(accessToken)) {
                const newToken = await refreshAccessToken(accessToken, refreshToken);
                if (newToken) {
                    config.headers["Authorization"] = "Bearer " + newToken;
                } else {
                    // If refresh failed, the function already handles logout
                    return config;
                }
            } else {
                config.headers["Authorization"] = "Bearer " + accessToken;
            }
        }

        return config;
    }, (error) => {
        return Promise.reject(error);
    });

    return client;
};

const isTokenValid = (token: string): boolean => {
    try {
        const decodedToken: DecodedToken = jwtDecode(token);
        const currentTime = Date.now();
        const expirationTime = decodedToken.exp * 1000;
        const refreshTime = expirationTime - 0 * 1000; // 0 sec buffer
        return currentTime < refreshTime;
    } catch {
        return false;
    }
};

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const refreshAccessToken = async (oldToken: string, refreshToken: string) => {
    const maxRetries = 3;
    const retryDelay = 500; // 500ms

    for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
            // Before making refresh call, check if token was updated by another tab
            if (attempt > 0) {
                await sleep(retryDelay);

                const currentToken = localStorage.getItem("token");
                const currentRefreshToken = localStorage.getItem("refreshToken");
                const refreshTokenExpiry = localStorage.getItem("refreshTokenExpiryTime");

                // If we have a different token and it's valid, use it
                if (currentToken && currentToken !== oldToken && isTokenValid(currentToken) &&
                    currentRefreshToken && refreshTokenExpiry && new Date(refreshTokenExpiry) > new Date()) {
                    console.log(`Token was refreshed by another tab on attempt ${attempt + 1}`);
                    return currentToken;
                }

                // If token is still the same and expired, continue with refresh
                if (currentToken && !isTokenValid(currentToken)) {
                    console.log(`Token still expired on attempt ${attempt + 1}, proceeding with refresh`);
                }
            }

            const response = await axios.post(`${endpointRest.replace("/v1", "")}/token/refresh`, {
                'token': oldToken,
                'refreshToken': refreshToken,
            });

            if (response.status === 204) {
                console.log("Token not expired yet since we got 204. Returning token from localStorage...");
                return localStorage.getItem("token");
            }

            if (response.status !== 200) {
                console.log(`Failed to refresh token on attempt ${attempt + 1}:`, response.status);
                if (response.status === 401) {
                    if (attempt === maxRetries - 1) {
                        localStorage.removeItem("token");
                        localStorage.removeItem("refreshToken");
                        localStorage.removeItem("refreshTokenExpiryTime");
                        window.location.href = "/login";
                        throw new Error('Failed to refresh token after all retries');
                    }
                    continue; // Retry
                }
                return null;
            }

            localStorage.setItem("token", response.data.token);
            localStorage.setItem("refreshToken", response.data.refreshToken);
            localStorage.setItem("refreshTokenExpiryTime", response.data.refreshTokenExpiryTime);

            console.log(`Token refreshed successfully on attempt ${attempt + 1}`);
            return response.data.token;
        } catch (error: any) {
            console.log(`Error refreshing token on attempt ${attempt + 1}:`, error);

            if (error.response && error.response.status === 401) {
                if (attempt === maxRetries - 1) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("refreshToken");
                    localStorage.removeItem("refreshTokenExpiryTime");
                    window.location.href = "/login";
                    return null;
                }
                continue; // Retry
            }

            // For non-401 errors, don't retry
            return null;
        }
    }

    return null;
};

export default ApiHttpClient;
